import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Wrench } from "lucide-react";
import type { PageIssue } from "@/types/technicalAudit";

interface MissingCompressionCardProps {
	issue: PageIssue;
}

// #11 (PDF 2.7): compression is a server/CDN config change — not auto-applicable.
// Surface a clear recommendation so the audit still adds value for the client/dev.
export function MissingCompressionCard({ issue }: MissingCompressionCardProps) {
	const fix = issue.proposed_fix as { recommendation?: string } | null;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Uncompressed assets detected</h4>
					<p className="text-sm mt-1 text-amber-800 dark:text-amber-300 break-all">{issue.page_url}</p>
				</div>
			</div>

			<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
							<Wrench className="w-4 h-4 text-green-600 dark:text-green-400" />
						</div>
						Recommended Action
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
						<p className="text-sm text-green-800 dark:text-green-300 font-medium">
							Enable GZIP or Brotli compression at the server or CDN level.
						</p>
						<p className="text-sm text-green-800 dark:text-green-300 mt-1">
							Configure it in your web server (Nginx/Apache) or CDN/host settings.
							{fix?.recommendation ? ` ${fix.recommendation}` : ""}
						</p>
					</div>
					<div className="flex items-center gap-2">
						<Badge variant="outline" className="bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800">
							Server configuration — not auto-applied
						</Badge>
					</div>
					<p className="text-xs text-muted-foreground">
						This is saved as a recommendation in Technical Audit; it does not change
						WordPress.
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
